import { useLanguage } from "@/contexts/LanguageContext";
import { Zap, Euro, RefreshCw, ShieldCheck } from "lucide-react";
import AnimateOnScroll from "@/components/AnimateOnScroll";

const AIAdvantageSection = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Zap,
      statStart: 3,
      statEnd: 7,
      isRange: true,
      unit: t('whatYouGet.days'),
      label: t('whatYouGet.speed.label'),
      gradient: "from-primary via-primary/80 to-primary/60"
    },
    {
      icon: Euro,
      prefix: t('whatYouGet.from') + " €",
      statEnd: 799,
      isRange: false,
      unit: "",
      label: t('whatYouGet.price.label'),
      gradient: "from-secondary via-secondary/80 to-secondary/60"
    },
    {
      icon: RefreshCw,
      statStart: 2,
      statEnd: 5,
      isRange: true,
      unit: t('whatYouGet.revisionsUnit'),
      label: t('whatYouGet.revisions.label'),
      gradient: "from-accent via-accent/80 to-accent/60"
    },
    {
      icon: ShieldCheck,
      statEnd: 100,
      suffix: "%",
      isRange: false,
      unit: "",
      label: t('whatYouGet.guarantee.label'),
      gradient: "from-primary via-primary/80 to-primary/60"
    }
  ];

  return (
    <section id="ai-advantage" className="py-12 md:py-20 lg:py-24 relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background overflow-hidden" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-8 md:mb-12 lg:mb-16 pt-4">
          <AnimateOnScroll variant="up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-outline mb-4 md:mb-6 text-foreground text-center uppercase px-2 leading-tight" style={{ fontWeight: 900 }}>
              {t('whatYouGet.title').split(' ').slice(0, 1).join(' ')}{' '}
              <span className="relative inline-block">
                <span className="relative z-10">{t('whatYouGet.title').split(' ').slice(1).join(' ')}</span>
                <span 
                  className="absolute bottom-2 left-0 right-0 h-4 -rotate-1 -z-0"
                  style={{
                    backgroundColor: 'hsl(11, 86%, 55%)',
                    background: 'linear-gradient(104deg, rgba(239, 106, 55, 0) 0.9%, rgba(239, 106, 55, 1) 2.4%, rgba(239, 106, 55, 0.6) 5.8%, rgba(239, 106, 55, 0.1) 93%, rgba(239, 106, 55, 0.8) 96%, rgba(239, 106, 55, 0) 98%), linear-gradient(183deg, rgba(239, 106, 55, 0) 0%, rgba(239, 106, 55, 0.3) 7.9%, rgba(239, 106, 55, 0) 15%)'
                  }}
                />
              </span>
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll variant="up" delay={100}>
            <p className="text-lg sm:text-xl md:text-2xl text-foreground/80 max-w-3xl mx-auto font-medium text-center px-2">
              {t('whatYouGet.subtitle')}
            </p>
          </AnimateOnScroll>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <AnimateOnScroll
                key={index}
                variant="zoom"
                delay={200 + index * 100}
              >
                <div className="group relative h-full">
                {/* Card */}
                <div className="relative h-full bg-white border-2 border-border rounded-2xl p-5 md:p-6 lg:p-8 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-primary/20">
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`} />
                  
                  {/* Icon */}
                  <div className="relative mb-6 flex justify-center">
                    <div className={`p-6 rounded-xl bg-gradient-to-br ${benefit.gradient} shadow-xl`}>
                      <Icon className="w-12 h-12 text-white" strokeWidth={2.5} />
                    </div>
                  </div>

                  {/* Stat */}
                  <div className="relative text-center mb-4">
                    <div className="flex items-baseline justify-center gap-1">
                      {benefit.isRange ? (
                        <>
                          <span className="text-4xl md:text-5xl font-bold text-foreground">
                            {benefit.statStart}
                          </span>
                          <span className="text-4xl md:text-5xl font-bold text-foreground">-</span>
                          <span className="text-4xl md:text-5xl font-bold text-foreground">
                            {benefit.statEnd}
                          </span>
                        </>
                      ) : (
                        <span className="text-4xl md:text-5xl font-bold text-foreground">
                          {benefit.prefix}{benefit.statEnd}{benefit.suffix}
                        </span>
                      )}
                      {benefit.unit && (
                        <span className="text-lg md:text-xl font-semibold text-foreground/80">
                          {benefit.unit}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Label */}
                  <p className="relative text-center text-lg font-medium text-foreground">
                    {benefit.label}
                  </p>
                </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AIAdvantageSection;
